// Audio: duel sound-effects (served from R2 like all art) + player-supplied background audio.
//
// The game plays NO background audio by default, it sounds only if a player uploads their own.
// Those uploads are stored LOCALLY in the browser's IndexedDB (the actual bytes live in the user's
// profile, never on our servers) and played back via object URLs. SFX, by contrast, are the game's
// own short .wav clips fetched from the asset CDN (audio/se/*.wav).

import { assetUrl } from "../config.ts";
import { settings } from "./settings.svelte.ts";

// ── local audio storage (IndexedDB, blobs, keyed by auto-id) ───────────────────
// "duel-losing" is the duel's alternate queue, played while the local player is behind on LP.
export type AudioScene = "duel" | "overworld" | "duel-losing";
export interface AudioItem { id: number; scene: AudioScene; name: string; blob: Blob; }

const DB_NAME = "df.audio";
const STORE = "items";
let dbp: Promise<IDBDatabase> | null = null;
function db(): Promise<IDBDatabase> {
  return (dbp ??= new Promise((res, rej) => {
    const r = indexedDB.open(DB_NAME, 2);
    r.onupgradeneeded = () => {
      const d = r.result;
      if (!d.objectStoreNames.contains(STORE)) d.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
    };
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  }));
}
function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> {
  return db().then(
    (d) =>
      new Promise<T>((res, rej) => {
        const req = fn(d.transaction(STORE, mode).objectStore(STORE));
        req.onsuccess = () => res(req.result as T);
        req.onerror = () => rej(req.error);
      }),
  );
}

export async function listAudio(scene: AudioScene): Promise<AudioItem[]> {
  // ponytail: getAll + filter, a scene holds a handful of items; an index isn't worth it.
  const all = await tx<AudioItem[]>("readonly", (s) => s.getAll());
  return all.filter((a) => a.scene === scene);
}
export async function addAudio(scene: AudioScene, file: File): Promise<void> {
  await tx("readwrite", (s) => s.add({ scene, name: file.name, blob: file }));
  if (scene === activeScene) await reloadAudio(); // start/extend playback now if we're in that scene
}
export async function deleteAudio(id: number, scene: AudioScene): Promise<void> {
  await tx("readwrite", (s) => s.delete(id));
  if (scene === activeScene) await reloadAudio();
}

// ── sound effects ───────────────────────────────────────────────────────────────
// One preloaded template per clip (warms the HTTP cache); each play uses a clone so rapid events
// (a 5-card Set, a chain) can overlap instead of cutting each other off.
const seCache = new Map<string, HTMLAudioElement>();
function seTemplate(name: string): HTMLAudioElement {
  let el = seCache.get(name);
  if (!el) {
    el = new Audio(assetUrl(`audio/se/${name}.wav`));
    el.preload = "auto";
    seCache.set(name, el);
  }
  return el;
}
/** Play a duel sound-effect by clip name (audio/se/<name>.wav). No-op if SFX are off. */
export function sfx(name: string): void {
  if (!settings.sfxEnabled || settings.sfxVolume <= 0) return;
  const el = seTemplate(name).cloneNode() as HTMLAudioElement; // clone copies src → overlapping plays
  el.volume = settings.sfxVolume;
  void el.play().catch(() => {}); // no user-gesture yet → browser blocks autoplay; ignore
}

// ── background audio (player uploads, looped as a queue) ──────────────────────────
let audioEl: HTMLAudioElement | null = null;
let activeScene: AudioScene | null = null;
let duelLosing = false; // true once the local player is behind on LP, swaps the duel queue
let queue: { url: string }[] = [];
let idx = 0;

function stopAudio(): void {
  if (audioEl) {
    audioEl.onended = null;
    audioEl.pause();
    audioEl = null;
  }
  for (const q of queue) URL.revokeObjectURL(q.url);
  queue = [];
  idx = 0;
}

function playCurrent(): void {
  const item = queue[idx];
  if (!item) return;
  audioEl = new Audio(item.url);
  audioEl.volume = settings.audioVolume;
  audioEl.onended = () => {
    idx = (idx + 1) % queue.length; // loop the queue
    playCurrent();
  };
  void audioEl.play().catch(() => {});
}

async function reloadAudio(): Promise<void> {
  stopAudio();
  if (!activeScene) return;
  // While behind in a duel, play the "losing" queue instead; fall back to the regular duel clips if
  // none were uploaded, so the player still hears their audio rather than silence.
  const losing = activeScene === "duel" && duelLosing;
  let items = await listAudio(losing ? "duel-losing" : activeScene);
  if (!items.length && losing) items = await listAudio("duel");
  if (!items.length) return; // no uploads → silence (the default)
  queue = items.map((a) => ({ url: URL.createObjectURL(a.blob) }));
  playCurrent();
}

/** Switch which background-audio queue is active. Driven by the current screen (App.svelte):
 *  "overworld" in the world, "duel" in a duel, null (silence) everywhere else. */
export function setAudioScene(scene: AudioScene | null): void {
  if (scene === activeScene) return;
  activeScene = scene;
  duelLosing = false; // each scene entry starts on the regular queue
  void reloadAudio();
}

/** Toggle the duel's "losing" audio queue. Called from the duel store as LP changes: the player is
 *  losing once their life points fall below the opponent's. No-op outside a duel / on no change. */
export function setDuelLosing(losing: boolean): void {
  if (losing === duelLosing) return;
  duelLosing = losing;
  if (activeScene === "duel") void reloadAudio();
}

/** Live audio-volume change (drag the slider), apply to the currently-playing item too. */
export function applyAudioVolume(): void {
  if (audioEl) audioEl.volume = settings.audioVolume;
}
